import { throwErrorWithId } from '@pypestream/utils';

import { getCustomerPypes, toBotRetry } from '../../utils';
// Deep import, not the barrel: DEFAULTS below is evaluated at module load, and
// the barrel sits in an import cycle (utils/index -> sync-xstate-with-route-params
// -> app.xstate -> prostudio.xstate -> invokes/index -> here), so reading the
// constant through it can hit the TDZ. `pype-agent-routing` imports only types.
import { DEFAULT_BOT_RETRY } from '../../utils/pype-agent-routing';
import { Pype } from '../prostudio.api-service.types';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

type Env = 'live' | 'sandbox';

export interface ErrorHandlingValues {
  /** Timeout in seconds (the form unit); stored as ms on the pype. */
  timeout: number;
  attempts: number;
}

export interface ErrorHandlingData {
  errorHandling: { live: ErrorHandlingValues; sandbox: ErrorHandlingValues };
  liveAgentPypes: { live: Pype[]; sandbox: Pype[] };
}

export const MS_PER_SECOND = 1000;

/**
 * Shown when an env has no channels at all. Derived from the same pype_api
 * fallbacks `toBotRetry` uses, so the "no channels" and "channel without
 * bot_retry" paths cannot drift apart. Exported because the update invoke needs
 * the same pair when `ctx.errorHandling` is null.
 *
 * `Math.ceil` matches the live path below: were the default ever a fractional
 * number of seconds, a raw divide would render e.g. "35.5" into a field whose
 * validation rejects non-integers, disabling Update on arrival.
 */
export const DEFAULT_ERROR_HANDLING_VALUES: ErrorHandlingValues = {
  timeout: Math.ceil(DEFAULT_BOT_RETRY.timeout / MS_PER_SECOND),
  attempts: DEFAULT_BOT_RETRY.attempts,
};

/**
 * Reads bot_retry for display. The Error Handling form is a single
 * Timeout/Attempts pair applied to every connection, but the value lives per
 * pype, so the env's channels can disagree.
 *
 * PSTUDIO-3949: surface the LARGEST timeout and the LARGEST attempts found in
 * the env, each maxed independently. Reading one representative pype (this used
 * to take `[0]`) meant pressing Update silently shrank every other channel's
 * retry budget down to whatever the V6 list happened to return first — or reset
 * the whole env to the defaults when that pype had no bot_retry. Nothing is
 * written here; the consolidation lands only when the user presses Update.
 *
 * NOTE: bot_retry.timeout is stored in ms; the form shows seconds.
 */
export const loadErrorHandling = async (
  ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<ErrorHandlingData> => {
  const projectId = ctx.project?.id;
  const customerId = ctx.project?.projectProStudioSetting?.customerId;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
  }
  if (!customerId) {
    throwErrorWithId('Customer ID is required');
  }

  const liveAgentPypes = await getCustomerPypes({
    uniqueCustomerId: customerId!,
    projectId: projectId!,
  });

  // bot_retry ships with the V6 pype list, so no extra request per env.
  const readEnv = (env: Env): ErrorHandlingValues => {
    // Only id-bearing pypes: the bulk write filters the same way
    // (see update-error-handling), so the read scope must match the write scope.
    const retries = (liveAgentPypes[env] ?? [])
      .filter((pype) => !!pype.id)
      // Normalized, not raw: a pype with no bot_retry really does behave as
      // pype_api's defaults at runtime, so that is the value that competes.
      .map((pype) => toBotRetry(pype.bot_retry));

    if (retries.length === 0) return DEFAULT_ERROR_HANDLING_VALUES;

    return {
      // Ceil, not round: a sub-second remainder must not display BELOW the
      // largest stored timeout, or Update would write the shrunken value back.
      timeout: Math.ceil(
        Math.max(...retries.map((retry) => retry.timeout)) / MS_PER_SECOND
      ),
      attempts: Math.max(...retries.map((retry) => retry.attempts)),
    };
  };

  return {
    errorHandling: { live: readEnv('live'), sandbox: readEnv('sandbox') },
    liveAgentPypes,
  };
};
